/**
 * Per-event Main Clue / Sponsor Run config: A/B/C set assignments and sponsor → question id lists.
 * Replaces legacy localStorage (freshmeet2026-hq-sponsor-sets-v1:{eventId}) for set assignments.
 */

import {
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { firestore } from '../firebase';

export const MAIN_CLUE_EVENT_CONFIG = 'mainClueEventConfig';

/** Legacy localStorage key prefix (must match FreshMeetHqDashboard / Printables history). */
export const LEGACY_ASSIGN_STORAGE_PREFIX = 'freshmeet2026-hq-sponsor-sets-v1';

export function readLegacySetAssignmentsFromLocalStorage(eventId) {
  if (typeof window === 'undefined' || !eventId) return {};
  try {
    const raw = window.localStorage.getItem(`${LEGACY_ASSIGN_STORAGE_PREFIX}:${eventId}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out = {};
    Object.keys(parsed).forEach((k) => {
      const v = parsed[k];
      if (v === 'A' || v === 'B' || v === 'C') out[k] = v;
    });
    return out;
  } catch {
    return {};
  }
}

export function clearLegacySetAssignmentsLocalStorage(eventId) {
  if (typeof window === 'undefined' || !eventId) return;
  try {
    window.localStorage.removeItem(`${LEGACY_ASSIGN_STORAGE_PREFIX}:${eventId}`);
  } catch {
    // ignore
  }
}

function normalizeSetAssignments(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  Object.keys(raw).forEach((k) => {
    const v = raw[k];
    if (v === 'A' || v === 'B' || v === 'C') out[k] = v;
  });
  return out;
}

function normalizeSponsorQuestionIds(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  Object.keys(raw).forEach((k) => {
    const arr = raw[k];
    if (!Array.isArray(arr)) return;
    out[k] = arr.map((id) => String(id || '').trim()).filter(Boolean);
  });
  return out;
}

/**
 * @returns {Promise<{ setAssignments: Record<string,'A'|'B'|'C'>, sponsorQuestionIds: Record<string, string[]> }>}
 */
export async function getMainClueEventConfig(eventId) {
  const id = (eventId || '').toString().trim();
  if (!id) {
    return { setAssignments: {}, sponsorQuestionIds: {} };
  }
  const snap = await getDoc(doc(firestore, MAIN_CLUE_EVENT_CONFIG, id));
  if (!snap.exists()) {
    return { setAssignments: {}, sponsorQuestionIds: {} };
  }
  const d = snap.data() || {};
  return {
    setAssignments: normalizeSetAssignments(d.setAssignments),
    sponsorQuestionIds: normalizeSponsorQuestionIds(d.sponsorQuestionIds),
  };
}

/**
 * One-time migration: if Firestore has no set assignments, copy from localStorage and persist.
 * @returns {Promise<{ setAssignments: Record<string,'A'|'B'|'C'>, sponsorQuestionIds: Record<string, string[]>, migrated: boolean }>}
 */
export async function ensureMainClueEventConfigMigrated(eventId) {
  const id = (eventId || '').toString().trim();
  if (!id) {
    return { setAssignments: {}, sponsorQuestionIds: {}, migrated: false };
  }
  const ref = doc(firestore, MAIN_CLUE_EVENT_CONFIG, id);
  const snap = await getDoc(ref);
  const existing = snap.exists() ? snap.data() || {} : {};
  const existingSets = normalizeSetAssignments(existing.setAssignments);
  const sponsorQuestionIds = normalizeSponsorQuestionIds(existing.sponsorQuestionIds);

  if (Object.keys(existingSets).length > 0) {
    return { setAssignments: existingSets, sponsorQuestionIds, migrated: false };
  }

  const legacy = readLegacySetAssignmentsFromLocalStorage(id);
  if (Object.keys(legacy).length === 0) {
    return { setAssignments: {}, sponsorQuestionIds, migrated: false };
  }

  await setDoc(
    ref,
    {
      setAssignments: legacy,
      sponsorQuestionIds: existing.sponsorQuestionIds || {},
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  clearLegacySetAssignmentsLocalStorage(id);
  return { setAssignments: legacy, sponsorQuestionIds, migrated: true };
}

/**
 * @param {(data: { setAssignments: Record<string,'A'|'B'|'C'>, sponsorQuestionIds: Record<string, string[]> }) => void} onData
 * @returns {() => void} unsubscribe
 */
export function subscribeMainClueEventConfig(eventId, onData) {
  const id = (eventId || '').toString().trim();
  if (!id) {
    onData({ setAssignments: {}, sponsorQuestionIds: {} });
    return () => {};
  }
  return onSnapshot(doc(firestore, MAIN_CLUE_EVENT_CONFIG, id), (snap) => {
    if (!snap.exists()) {
      onData({ setAssignments: {}, sponsorQuestionIds: {} });
      return;
    }
    const d = snap.data() || {};
    onData({
      setAssignments: normalizeSetAssignments(d.setAssignments),
      sponsorQuestionIds: normalizeSponsorQuestionIds(d.sponsorQuestionIds),
    });
  });
}

/**
 * @param {Record<string, 'A'|'B'|'C'|null|undefined>} assignments sponsorId -> set or null to clear
 */
export async function saveSetAssignments(eventId, assignments) {
  const id = (eventId || '').toString().trim();
  if (!id) return;
  const setAssignments = {};
  Object.keys(assignments || {}).forEach((k) => {
    const v = assignments[k];
    if (v === 'A' || v === 'B' || v === 'C') setAssignments[k] = v;
  });
  await setDoc(
    doc(firestore, MAIN_CLUE_EVENT_CONFIG, id),
    {
      setAssignments,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Replace entire sponsorQuestionIds map (caller merges in memory if needed).
 */
export async function saveSponsorQuestionIds(eventId, sponsorQuestionIds) {
  const id = (eventId || '').toString().trim();
  if (!id) return;
  await setDoc(
    doc(firestore, MAIN_CLUE_EVENT_CONFIG, id),
    {
      sponsorQuestionIds: normalizeSponsorQuestionIds(sponsorQuestionIds),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Update one sponsor's ordered question ids; preserves other fields via merge.
 */
export async function saveSponsorQuestionIdsForSponsor(eventId, sponsorId, questionIds) {
  const id = (eventId || '').toString().trim();
  const sid = (sponsorId || '').toString().trim();
  if (!id || !sid) return;
  const current = await getMainClueEventConfig(id);
  const next = { ...current.sponsorQuestionIds, [sid]: (questionIds || []).map((q) => String(q).trim()).filter(Boolean) };
  await saveSponsorQuestionIds(id, next);
}

/**
 * Merge set assignment for one sponsor into Firestore config.
 */
export async function patchSetAssignmentForSponsor(eventId, sponsorId, setId) {
  const id = (eventId || '').toString().trim();
  const sid = (sponsorId || '').toString().trim();
  if (!id || !sid) return;
  const current = await getMainClueEventConfig(id);
  const next = { ...current.setAssignments };
  if (setId === 'A' || setId === 'B' || setId === 'C') {
    next[sid] = setId;
  } else {
    delete next[sid];
  }
  await saveSetAssignments(id, next);
}
